import { useEffect, useState } from 'react';
import { ChainId, WRAPPED_SOL } from '@shared/utils/config/networks';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@radix-ui/react-tooltip';
import { IoIosArrowRoundDown, IoMdInformationCircleOutline } from 'react-icons/io';
import { networkNames } from '@shared/types/executor';
import { LiaExchangeAltSolid } from 'react-icons/lia';
import { useRouter } from 'next/navigation';
import { V2Networks } from '@shared/utils/config/networkAddresses';
import { AggregatorResponse } from '@shared/types/services/aggregator-response';
import { BridgeResponse } from '@shared/types/services/bridge-response';
import TransactionHashes from './TransactionHashes';
import classNames from '@/lib/misc/classNames';
import { getNetworkLogo } from '@/lib/misc/tokenImageHelper';
import getFeesFromQuote from '@/lib/trade/getFeesFromQuote';
import { Button } from '@/components/ui/button';
import { toSpecialFixed } from '@/lib/misc/utils';
import useAllowShowTooltip from '@/hooks/utilities/useAllowShowTooltip';
import { isNativeTokenAddress, SOLANA_NATIVE_ADDRESS, SOLANA_NATIVE_SIMPLE } from '@/lib/asset/nativeAddressHelper';
import TokenImage from '@/components/shared/TokenImage';
export default function TransactionView({
  txItem,
  isModalOpen
}) {
  const router = useRouter();
  const allowTooltip = useAllowShowTooltip();
  const settledTokensOut = txItem?.settled_tokens_out;
  const tokensIn = txItem?.token_prices?.tokensIn || [];
  const tokensOut = txItem?.token_prices?.tokensOut || [];
  const error = txItem?.status == 'error' || txItem?.result?.decodedResponse?.status != 'success';
  const {
    geniusFees,
    gasFees,
    netSponsorFees,
    bridgeFees,
    sponsorFees,
    netNativeSponsorAmount,
    networkName,
    network
  } = getFeesFromQuote({
    quoteData: {
      response: [txItem?.order] as AggregatorResponse[] | BridgeResponse[]
    },
    legacyQuoteData: {
      quote: txItem?.order
    }
  });
  const [showTxHashes, setShowTxHashes] = useState(false);
  const [showModal, setShowModal] = useState(false);
  useEffect(() => {
    let timer;
    if (isModalOpen) {
      timer = setTimeout(() => setShowModal(true), 500);
    } else {
      setShowModal(false);
      setShowTxHashes(false);
    }
    return () => clearTimeout(timer);
  }, [isModalOpen]);
  const logoUrls: {
    tokenAddress: string;
    logoUrl: string;
    network: number;
  }[] = [...tokensIn, ...tokensOut].map(token => {
    return {
      tokenAddress: token.contract_address,
      logoUrl: token.logo_url,
      network: token.network
    };
  });
  const inputAggregators = txItem?.order?.result?.aggregator ? [{
    aggregator: txItem?.order?.result?.aggregator?.toLowerCase(),
    network: txItem?.order?.result?.price?.network
  }] : txItem?.order?.results?.length ? txItem?.order?.results?.map(x => {
    return {
      aggregator: x?.quote?.aggregator?.toLowerCase(),
      network: x?.quote?.network == 7565164 ? ChainId.SOLANA : x?.quote?.network
    };
  }) : txItem?.order?.result?.tokenInPrices?.map(x => {
    return {
      aggregator: x?.aggregator?.toLowerCase(),
      network: x?.price?.network
    };
  });
  const outputAggregators = txItem?.result?.decodedResponse?.type == 'SWAP_DLN' ? txItem?.settled_tokens_out?.map(x => {
    return {
      aggregator: 'Debridge',
      network: x.network
    };
  }) : txItem?.result?.decodedResponse?.type == 'SWAP_OKX' ? txItem?.settled_tokens_out?.map(x => {
    return {
      aggregator: 'OKX Bridge',
      network: x.network
    };
  }) : txItem?.order?.result?.tokenOutPrices?.length ? txItem?.order?.result?.tokenOutPrices?.map(x => {
    return {
      aggregator: x?.aggregator?.toLowerCase(),
      network: x?.price?.network
    };
  }) : [{
    aggregator: txItem?.order?.result?.aggregator?.toLowerCase(),
    network: txItem?.order?.result?.price?.network
  }];
  return showTxHashes ? <div className='mb-6 w-full bg-genius-indigo px-4' data-sentry-component="TransactionView" data-sentry-source-file="TransactionView.tsx">
      <TransactionHashes txItem={txItem} setShowTxHashes={setShowTxHashes} data-sentry-element="TransactionHashes" data-sentry-source-file="TransactionView.tsx" />
    </div> : <div className='mb-6 w-full bg-genius-indigo px-4' data-sentry-component="TransactionView" data-sentry-source-file="TransactionView.tsx">
      <>
        <div className='w-full flex justify-between items-center h-12'>
          <div className='flex items-center ta-c w-full'>
            <div className='w-full flex ta-c text-[16px]'>
              <p className={`mx-auto text-[16px] flex items-center ${txItem?.order_type?.includes('Buy') ? 'text-green-300' : txItem?.order_type?.includes('Sell') ? 'text-red-300' : 'text-genius-cream'} capitalize`}>
                <LiaExchangeAltSolid className='w-[18px] h-[18px] mr-2 text-genius-cream' data-sentry-element="LiaExchangeAltSolid" data-sentry-source-file="TransactionView.tsx" />
                {txItem?.type?.replace('_', ' ').replace('withdraw', 'Send')?.toLowerCase()}
                {txItem?.order_type?.replace('_', ' ').replace('MARKET', ' ').replace('withdraw', 'Send')?.toLowerCase()}
              </p>
            </div>
          </div>
        </div>
        <div className='mt-2 mx-auto overflow-hidden py-2'>
          <div className={`w-full justify-center rounded-xl px-4 py-2 ${!error ? 'bg-genius-blue' : 'bg-[#2c1126] border-[1px] border-red-400'}`}>
            {logoUrls?.map((x, index) => {
            const network = x?.network;
            const tokenAddress = x?.tokenAddress === SOLANA_NATIVE_SIMPLE ? SOLANA_NATIVE_ADDRESS : x?.tokenAddress;
            return <>
                  <div className={classNames(`relative flex w-full items-center justify-between`)}>
                    <div className='relative w-fit my-2'>
                      <div className='cursor-pointer' onClick={() => {
                    router.push(`/asset?address=${tokenAddress}&networkId=${network}`);
                  }}>
                        <TokenImage mainImageSrc={isNativeTokenAddress(tokenAddress) ? getNetworkLogo(network) : x.logoUrl} badgeImageSrc={getNetworkLogo(network)} size={32} badgeSize={16} />
                      </div>
                    </div>
                    <div className='flex items-center justify-between w-[85%]'>
                      {index === 0 ? tokensIn?.map((x, idx) => {
                    const aggregator = inputAggregators?.find(y => y?.network === x?.network);
                    const usdValue = x?.amountToUse * parseFloat(x?.quote_rate?.toString());
                    const tokensUsed = x?.amountToUse;
                    return <div key={idx} className='flex w-full justify-between items-center'>
                                <div className='w-2/4'>
                                  <p className='text-[16px] flex items-center'>
                                    {x?.contract_ticker_symbol}
                                  </p>
                                  {networkNames[aggregator?.network] && <p className='text-[10px] text-genius-cream/50'>
                                      Via{' '}
                                      <span className='capitalize mr-1'>
                                        {aggregator?.aggregator}
                                      </span>
                                      on{' '}
                                      <span className='capitalize'>
                                        {networkNames[aggregator?.network]}
                                      </span>
                                    </p>}
                                </div>

                                <div className='w-2/4 ta-r'>
                                  <p className='text-[16px]  items-end w-full'>
                                    {toSpecialFixed(tokensUsed, 2)?.value}{' '}
                                    {x?.contract_ticker_symbol}
                                  </p>
                                  <p className='text-[10px] text-genius-cream/50'>
                                    ${toSpecialFixed(usdValue, 2)?.value} USD
                                  </p>
                                </div>
                              </div>;
                  }) : settledTokensOut?.map((x, idx) => {
                    const aggregator = outputAggregators?.find(y => y?.network === x?.network);
                    const price = txItem?.token_out_prices?.find(y => y?.token?.address?.toLowerCase() === x?.token_address?.toLowerCase());
                    const networkName = networkNames[aggregator?.network];
                    return <>
                                {idx === 1 && network === ChainId.SOLANA && x.token_address === WRAPPED_SOL ? <></> : <div className='justify-between flex items-center w-full'>
                                    <div className='w-3/4 ta-l'>
                                      <p className='text-[16px] flex items-center'>
                                        {/* x.tokenAddress is for native assets */}
                                        {price?.token?.symbol || x?.token_address}
                                      </p>
                                      {networkName && <p className='text-[10px] text-genius-cream/50'>
                                          Via{' '}
                                          <span className='capitalize mr-1'>
                                            {aggregator?.aggregator}
                                          </span>
                                          on <span className='capitalize'>{networkName}</span>
                                        </p>}
                                    </div>
                                    <div className='w-2/4 ta-r'>
                                      <p className='text-[16px]'>
                                        {toSpecialFixed(x?.formatted_amount, 2)?.value}{' '}
                                        {price?.token?.symbol}
                                      </p>
                                      <p className='text-[10px] text-genius-cream/50'>
                                        ${toSpecialFixed(x?.usd_value, 3)?.value}
                                      </p>
                                    </div>
                                  </div>}
                              </>;
                  })}
                    </div>
                  </div>
                  {index === 0 && <IoIosArrowRoundDown className='ml-3 mys-2' />}
                </>;
          })}
            {netSponsorFees && netSponsorFees > 0 ? <div className='justify-between flex items-center w-full mt-2'>
                <div className='mr-1 my-2'>
                  <TokenImage mainImageSrc={getNetworkLogo(parseInt(network) == ChainId.SOLANA ? ChainId.SOLANA : 1)} badgeImageSrc={getNetworkLogo(parseInt(network))} size={32} badgeSize={16} />
                </div>
                <div className='w-3/4 ta-l'>
                  <p className='text-[16px] flex items-center'>
                    {V2Networks[network].nativeCurrency}
                  </p>
                  <p className='text-[10px] text-genius-cream/50'>
                    Via <span className='capitalize mr-1'>Genius Sponsorship</span>
                    on <span className='capitalize'>{networkName}</span>
                  </p>
                </div>
                <div className='w-2/4 ta-r flex-col flex'>
                  <p className='text-[16px] flex justify-end'>
                    <p>{toSpecialFixed(netNativeSponsorAmount, 3)?.value} </p>
                    <p className='ml-2'> {V2Networks[network].nativeCurrency}</p>
                  </p>

                  <p className='text-[10px] text-genius-cream/50'>
                    ${toSpecialFixed(netSponsorFees, 3)?.value}
                  </p>
                </div>
              </div> : <></>}
          </div>
        </div>

        <div className='py-4'>
          <div className='w-full text-genius-cream flex flex-col gap-2'>
            <div className='flex w-full justify-between'>
              <p className='text-[12px] text-genius-cream/50'>Status</p>
              <p className={`text-[12px] capitalize ${!error ? 'text-green-400' : 'text-red-400'}`}>
                {!error ? 'Success' : 'Failed'}
              </p>
            </div>
            <div className='flex w-full justify-between'>
              <p className='text-[12px] text-genius-cream/50'>Date</p>
              <p className='text-[12px]'>
                <span className='text-genius-cream/50'>
                  {new Date(txItem?.created_at).toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                  hour12: true
                })}{' '}
                  on{' '}
                </span>{' '}
                {new Date(txItem?.created_at).toLocaleDateString('en-US', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
              })}
              </p>
            </div>

            <div className='flex w-full justify-between'>
              <p className='text-[12px] text-genius-cream/50'>Execution Time</p>
              <p className='text-[12px] capitalize'>
                {toSpecialFixed(txItem?.execution_time / 1000, 2)?.value} seconds
              </p>
            </div>

            <div className='flex w-full justify-between'>
              <div className='flex items-center justify-start text-genius-cream/50 text-[12px]'>
                Genius Fee{' '}
              </div>

              <p className='text-[12px]'>
                {error ? 'No fee taken' : <>${toSpecialFixed(geniusFees, 2)?.value}</>}
              </p>
            </div>
          </div>

          {gasFees && !isNaN(gasFees) ? <div className='flex w-full justify-between mt-2'>
              <div className='flex items-center justify-start text-genius-cream/50 text-[12px]'>
                Gas Fee{' '}
                {allowTooltip && <TooltipProvider delayDuration={0}>
                    <Tooltip defaultOpen={false}>
                      <TooltipTrigger>
                        <IoMdInformationCircleOutline className='text-genius-cream/50 ml-2' size={12} />
                      </TooltipTrigger>
                      <TooltipContent className='p-0 ml-[400px]'>
                        <div className='text-xs text-genius-cream/50 max-w-sm bg-genius-blue p-2'>
                          Blockchains require gas to process transactions. Genius covers this cost
                          for you by sending your account a small amount of the native blockchain
                          token whenever you are running low. This is taken out automatically from
                          your swap and only happens when you are running low.
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>}
              </div>
              {error ? <p className='text-[12px]'>No fee taken.</p> : <p className='text-[12px] flex flex-col items-end justify-end'>
                  ${toSpecialFixed(gasFees, 2)?.value}
                  <>
                    {' '}
                    {!sponsorFees || parseFloat(network) != ChainId.SOLANA && !bridgeFees ? <p className='text-[10px] text-genius-cream/50'>
                        using your native balance
                        {bridgeFees && ' and token order'}
                      </p> : <p className='text-[10px] text-genius-cream/50'>using your token order</p>}
                  </>
                </p>}
            </div> : <></>}
          {bridgeFees && !isNaN(bridgeFees) ? <div className='w-full justify-between flex  mt-2'>
              <div className='flex items-center justify-start text-genius-cream/50 text-[12px]'>
                Bridge Fee{' '}
                {allowTooltip && <TooltipProvider delayDuration={100}>
                    <Tooltip>
                      <TooltipTrigger>
                        <IoMdInformationCircleOutline className='text-genius-cream/50 ml-2' size={12} />
                      </TooltipTrigger>
                      <TooltipContent className='p-0 ml-[400px]'>
                        <div className='text-xs text-genius-cream/50 max-w-sm bg-genius-blue p-2'>
                          The DeBridge Fee is taken by the DeBridge Network. Trading amounts less
                          than ~20 USD may not be profitable due to this fee. Genius will be
                          deploying it's own liquid matching protocol shortly to reduce this fee.
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>}
              </div>
              {error ? <div className='text-[12px]'>No fee taken</div> : <div className='text-[12px] flex flex-col items-end justify-end'>
                  ${toSpecialFixed(bridgeFees)?.value}
                  <>
                    {' '}
                    {!sponsorFees || parseFloat(network) != ChainId.SOLANA && !bridgeFees ? <p className='text-[10px] text-genius-cream/50'>
                        using your native balance
                        {bridgeFees && ' and token order'}
                      </p> : <p className='text-[10px] text-genius-cream/50'>using your token order</p>}
                  </>
                </div>}
            </div> : <></>}
        </div>
        {showModal ? <div className='w-full bg-genius-indigo'>
            <Button onClick={() => {
          setShowTxHashes(true);
        }} className='rounded-full w-full px-auto mx-auto h-[56px] hover:opacity-90 transition-opacity' variant='pink'>
              View on Explorer
            </Button>
          </div> : null}
      </>
    </div>;
}